<script>
import MarketplaceChannelTabs from "@/components/marketplace/MarketplaceChannelTabs.vue";
import ModalLoading from "@/components/other/ModalLoading.vue";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";
import { useDialog } from "@/services/dialogService";

const noSpaces = value => {
  if (!helpers.req(value)) return true;
  return !/\s/.test(String(value));
};

const sameId = (left, right) => String(left) === String(right);

export default {
  name: "MarketplaceConnectDialogContent",
  components: {
    MarketplaceChannelTabs,
    ModalLoading
  },
  props: {
    initialBranchId: {
      type: [String, Number],
      default: null
    },
    initialSalesChannelId: {
      type: [String, Number],
      default: null
    },
    isBottomSheet: {
      type: Boolean,
      default: false
    }
  },
  emits: ["action", "close"],
  setup() {
    const dialog = useDialog();
    return { v$: useVuelidate(), dialog };
  },
  validations() {
    const mustFillOrderId = function () {
      return this.form.sales_channel_id != 4;
    };

    return {
      form: {
        branch_id: { required },
        order_id: { required: requiredIf(mustFillOrderId), noSpaces }
      }
    };
  },
  data() {
    return {
      isLoadingMarketplace: true,
      loadError: "",
      marketList: [],
      marketObjList: [],
      channelTabs: [],
      connectedMarkets: [],
      selectedConnectedMarket: null,
      viewMode: "form",
      isSubmitting: false,
      form: {
        branch_id: null,
        sales_channel_id: null,
        order_id: ""
      }
    };
  },
  computed: {
    isDetailMode() {
      return this.viewMode === "detail" && this.selectedConnectedMarket;
    },
    activeChannelId() {
      if (this.isDetailMode) {
        return this.salesChannelId(this.selectedConnectedMarket);
      }
      return this.form.sales_channel_id;
    },
    lockedChannelId() {
      if (!this.initialBranchId || !this.initialSalesChannelId) return null;
      return this.initialSalesChannelId;
    },
    disabledIfConnect() {
      return Boolean(this.initialBranchId && this.initialSalesChannelId);
    },
    sheetTitle() {
      return this.$t("marketplace_channel");
    },
    detailChannelName() {
      return this.channelName(this.selectedConnectedMarket || {});
    },
    detailShopName() {
      const market = this.selectedConnectedMarket || {};
      return market.shopName || market.branch_name || "-";
    },
    detailStatus() {
      const market = this.selectedConnectedMarket || {};
      if (typeof market.status === "string") {
        const status = market.status.trim();
        if (
          status &&
          !["0", "1", "true", "false"].includes(status.toLowerCase())
        ) {
          return status;
        }
      }
      return this.$t("btn_connect_success_market");
    }
  },
  created() {
    this.getMarketPlace();
  },
  methods: {
    salesChannelId(channel) {
      return channel?.salesChannelId ?? channel?.sale_channel_id ?? channel?.id;
    },
    branchId(channel) {
      return channel?.branchId ?? channel?.branch_id;
    },
    channelName(channel) {
      const marketplaceName =
        typeof channel?.marketplace === "string" ? channel.marketplace : "";
      return (
        marketplaceName ||
        channel?.sale_channel_name ||
        channel?.name ||
        this.$t("marketplace_channel")
      );
    },
    isConnected(channel) {
      return channel && channel.status !== 0 && channel.status !== false;
    },
    buildChannelTabs(availableMarkets, disconnectedMarkets, connectedMarkets) {
      const tabs = new Map();
      const addTab = channel => {
        const id = this.salesChannelId(channel);
        if (id == null || id === "") return;

        const current = tabs.get(String(id)) || {};
        const marketplaceName =
          typeof channel.marketplace === "string" ? channel.marketplace : "";
        tabs.set(String(id), {
          ...current,
          ...channel,
          salesChannelId: id,
          marketplace:
            marketplaceName ||
            channel.sale_channel_name ||
            current.marketplace ||
            this.$t("marketplace_channel")
        });
      };

      availableMarkets.forEach(addTab);
      disconnectedMarkets.forEach(addTab);
      connectedMarkets.forEach(addTab);
      return Array.from(tabs.values()).slice(0, 4);
    },
    findConnectedMarket(salesChannelId, branchId = null) {
      return this.connectedMarkets.find(market => {
        if (!sameId(this.salesChannelId(market), salesChannelId)) return false;
        if (branchId == null || branchId === "")
          return this.isConnected(market);
        return (
          sameId(this.branchId(market), branchId) && this.isConnected(market)
        );
      });
    },
    async getMarketPlace() {
      this.isLoadingMarketplace = true;
      this.loadError = "";

      try {
        const data = await this.$axios("/api/v1/User/get/marketplace");
        if (data.result != 1) {
          this.loadError = this.$t("error_message");
          this.logMarketplaceLoadError(data.message || "Unexpected response");
          return;
        }

        const detail = data.detail || {};
        const availableMarkets = Array.isArray(detail.marketplace)
          ? detail.marketplace
          : [];
        const disconnectedMarkets = Array.isArray(detail.disconnect_marketplace)
          ? detail.disconnect_marketplace
          : [];

        this.connectedMarkets = Array.isArray(detail.connect_marketplace)
          ? detail.connect_marketplace
          : [];
        this.marketList = disconnectedMarkets.filter(channel => {
          if (availableMarkets.length === 0) return true;
          return availableMarkets.some(market =>
            sameId(this.salesChannelId(channel), this.salesChannelId(market))
          );
        });
        this.channelTabs = this.buildChannelTabs(
          availableMarkets,
          this.marketList,
          this.connectedMarkets
        );

        const connectedMarket =
          this.initialSalesChannelId != null
            ? this.findConnectedMarket(
                this.initialSalesChannelId,
                this.initialBranchId
              )
            : null;

        if (connectedMarket) {
          this.showConnectedDetail(connectedMarket);
          return;
        }

        const initialChannelId =
          this.initialSalesChannelId ?? this.salesChannelId(this.marketList[0]);
        if (initialChannelId != null) {
          this.showConnectForm(initialChannelId, this.initialBranchId);
        }
      } catch (error) {
        this.loadError = this.$t("error_message");
        this.logMarketplaceLoadError(error.message, error.response);
      } finally {
        this.isLoadingMarketplace = false;
      }
    },
    logMarketplaceLoadError(note, response = null) {
      this.$logger({
        response: response || {
          config: {
            url: "/api/v1/User/get/marketplace",
            methods: "GET",
            data: null
          }
        },
        action: {
          action: "load marketplace connection dialog",
          id: 3,
          note
        }
      });
    },
    showConnectedDetail(market) {
      this.viewMode = "detail";
      this.selectedConnectedMarket = market;
      this.v$.form.$reset();
    },
    showConnectForm(salesChannelId, branchId = null) {
      this.viewMode = "form";
      this.selectedConnectedMarket = null;
      this.form.sales_channel_id = salesChannelId;
      this.handleMarketPlace(salesChannelId, branchId);
      this.v$.form.$reset();
    },
    selectChannel(channel) {
      const salesChannelId = this.salesChannelId(channel);
      const connectedMarket = this.findConnectedMarket(salesChannelId);

      if (connectedMarket) {
        this.showConnectedDetail(connectedMarket);
        return;
      }

      if (!sameId(this.form.sales_channel_id, salesChannelId)) {
        this.form.order_id = "";
      }
      this.showConnectForm(salesChannelId);
    },
    handleMarketPlace(value, preferredBranchId = null) {
      const market = this.marketList.find(channel =>
        sameId(this.salesChannelId(channel), value)
      );
      this.marketObjList = Array.isArray(market?.marketplace)
        ? market.marketplace
        : [];

      if (
        preferredBranchId != null &&
        this.marketObjList.some(branch =>
          sameId(this.branchId(branch), preferredBranchId)
        )
      ) {
        this.form.branch_id = Number(preferredBranchId);
        return;
      }

      if (this.marketObjList.length === 1) {
        this.form.branch_id = this.branchId(this.marketObjList[0]);
        return;
      }

      const currentBranchExists = this.marketObjList.some(branch =>
        sameId(this.branchId(branch), this.form.branch_id)
      );
      if (!currentBranchExists) this.form.branch_id = null;
    },
    async addMarketPlace() {
      if (this.isSubmitting) return;

      this.v$.form.$touch();
      if (this.v$.form.$error) return;

      let shouldClose = false;
      this.isSubmitting = true;
      try {
        this.$refs.modalLoading?.show();
        const res = await this.$axios.post(
          "/api/v1/User/connect/marketplace",
          this.form
        );

        if (res.result == 1) {
          await this.dialog.showAlert({
            text: this.$t("success"),
            closeText: this.$t("close_window"),
            note: `- ${this.$t("note_market_2")}`
          });
          this.logMarketplaceResult("success", "");
          shouldClose = true;
        } else {
          await this.dialog.showError({ text: this.$t("error_message") });
          this.logMarketplaceResult("success but failed", res.message);
        }
      } catch (error) {
        await this.dialog.showError({ text: this.$t("error_message") });
        this.$logger({
          response: error.response || {
            config: {
              url: "/api/v1/User/connect/marketplace",
              methods: "POST",
              data: JSON.stringify(this.form)
            }
          },
          action: {
            action: "connect marketplace error",
            id: 3,
            note: error.message
          }
        });
      } finally {
        this.$refs.modalLoading?.hide();
        this.isSubmitting = false;
      }

      if (shouldClose) {
        this.$emit("action", {
          action: "connected",
          branchId: this.form.branch_id,
          salesChannelId: this.form.sales_channel_id
        });
      }
    },
    logMarketplaceResult(action, note) {
      this.$logger({
        response: {
          config: {
            url: "/api/v1/User/connect/marketplace",
            methods: "POST",
            data: JSON.stringify(this.form)
          }
        },
        action: {
          action,
          id: 3,
          note
        }
      });
    },
    closeSheet() {
      this.$emit("close");
    }
  }
};
</script>

<template>
  <section
    class="marketplace-connect-sheet"
    :class="{
      'is-detail': isDetailMode,
      'is-bottom-sheet': isBottomSheet
    }"
    data-test="marketplace-connect-sheet"
  >
    <header class="marketplace-connect-sheet__header">
      <h1 class="marketplace-connect-sheet__title">{{ sheetTitle }}</h1>
      <MarketplaceChannelTabs
        v-if="channelTabs.length"
        :channels="channelTabs"
        :active-id="activeChannelId"
        :locked-id="lockedChannelId"
        @select="selectChannel"
      />
    </header>

    <div
      v-if="isLoadingMarketplace"
      class="marketplace-connect-sheet__loading"
      data-test="marketplace-connect-loading"
      role="status"
      :aria-label="$t('loading')"
    >
      <Spinner />
    </div>

    <div
      v-else-if="loadError"
      class="marketplace-connect-sheet__error"
      data-test="marketplace-connect-error-msg"
      role="alert"
    >
      <p>{{ loadError }}</p>
      <c-btn
        variant="outline-primary"
        data-test="marketplace-connect-retry-btn"
        @click="getMarketPlace"
      >
        {{ $t("retry") }}
      </c-btn>
    </div>

    <template v-else-if="isDetailMode">
      <div
        class="marketplace-connect-sheet__body marketplace-connect-detail"
        data-test="marketplace-connect-detail"
      >
        <dl class="marketplace-connect-detail__list">
          <div class="marketplace-connect-detail__row">
            <dt>{{ $t("purchase_channel") }}</dt>
            <dd data-test="marketplace-connect-channel-value">
              {{ detailChannelName }}
            </dd>
          </div>
          <div class="marketplace-connect-detail__row">
            <dt>{{ $t("market_name") }}</dt>
            <dd data-test="marketplace-connect-store-value">
              {{ detailShopName }}
            </dd>
          </div>
          <div class="marketplace-connect-detail__row">
            <dt>{{ $t("status") }}</dt>
            <dd>
              <span
                class="marketplace-connect-detail__status"
                data-test="marketplace-connect-status-msg"
              >
                {{ detailStatus }}
              </span>
            </dd>
          </div>
        </dl>
      </div>

      <div class="marketplace-connect-sheet__actions">
        <c-btn
          block
          variant="confirm-primary"
          data-test="marketplace-connect-home-btn"
          @click="closeSheet"
        >
          {{ $t("reward_back_home") }}
        </c-btn>
      </div>
    </template>

    <template v-else-if="channelTabs.length === 0">
      <div
        class="marketplace-connect-sheet__empty"
        data-test="marketplace-connect-empty"
      >
        {{ $t("no_data") }}
      </div>
      <div class="marketplace-connect-sheet__actions">
        <c-btn
          block
          variant="outline-primary"
          data-test="marketplace-connect-empty-close-btn"
          @click="closeSheet"
        >
          {{ $t("cancel") }}
        </c-btn>
      </div>
    </template>

    <template v-else>
      <div
        class="marketplace-connect-sheet__body marketplace-connect-form"
        data-test="marketplace-connect-form"
      >
        <div class="marketplace-connect-form__benefit">
          <strong>
            {{ $t("note_market_1") }}
          </strong>
          <span>{{ $t("note_market_2") }}</span>
        </div>

        <InputSelect
          :title="$t('market_name')"
          :placeholder="$t('market_name')"
          valueField="branch_id"
          textField="branch_name"
          isRequired
          v-model:value="form.branch_id"
          :options="marketObjList"
          :disabled="
            disabledIfConnect ||
            form.sales_channel_id == null ||
            form.sales_channel_id === ''
          "
          :isValidate="v$.form.branch_id.$error"
          :v="v$.form.branch_id"
          :searchable="false"
          class="marketplace-connect-form__field"
          data-test="marketplace-connect-store-select"
        />

        <InputText
          :textFloat="$t('order_no')"
          :placeholder="$t('order_no')"
          type="text"
          :is-required="form.sales_channel_id != 4"
          v-model:value="v$.form.order_id.$model"
          :isValidate="v$.form.order_id.$error"
          :v="v$.form.order_id"
          :disabled="form.branch_id == null || form.branch_id === ''"
          class="marketplace-connect-form__field"
          data-test="marketplace-connect-order-input"
        />

        <small class="marketplace-connect-form__timestamp">
          {{ $t("date") }} : {{ $moment().format("D MMM YYYY HH:mm") }}
        </small>
      </div>

      <div class="marketplace-connect-sheet__actions is-stacked">
        <c-btn
          block
          variant="confirm-primary"
          :disabled="isSubmitting"
          data-test="marketplace-connect-confirm-btn"
          @click="addMarketPlace"
        >
          {{ $t("confirm") }}
        </c-btn>
        <c-btn
          block
          variant="outline-primary"
          data-test="marketplace-connect-cancel-btn"
          @click="closeSheet"
        >
          {{ $t("cancel") }}
        </c-btn>
      </div>
    </template>

    <ModalLoading ref="modalLoading" />
  </section>
</template>

<style lang="scss" scoped>
.marketplace-connect-sheet {
  display: flex;
  width: 100%;
  max-height: calc(100dvh - 72px - env(safe-area-inset-top));
  flex-direction: column;
  overflow: hidden;
  border-radius: 24px;

  color: var(--text-color);
}

.marketplace-connect-sheet.is-bottom-sheet {
  border-radius: 28px 28px 0 0;
}

.marketplace-connect-sheet.is-detail {
  min-height: 374px;
}

.marketplace-connect-sheet__header {
  flex: 0 0 auto;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--crm-border-color);
}

.marketplace-connect-sheet__title {
  margin: 0 0 16px;
  color: var(--text-color);
  font-size: var(--font-title);
  font-weight: 600;
  line-height: 1.4;
  text-align: center;
}

.marketplace-connect-sheet__body {
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: none;
}

.marketplace-connect-sheet__body::-webkit-scrollbar {
  display: none;
}

.marketplace-connect-sheet__loading,
.marketplace-connect-sheet__error,
.marketplace-connect-sheet__empty {
  display: flex;
  min-height: 220px;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.marketplace-connect-sheet__error {
  flex-direction: column;
  gap: 16px;
  text-align: center;
}

.marketplace-connect-sheet__empty {
  color: var(--text-secondery-color);
  text-align: center;
}

.marketplace-connect-sheet__error p {
  margin: 0;
}

.marketplace-connect-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px 0;
}

.marketplace-connect-form__benefit {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  border-radius: 8px;
  background: whitesmoke;
  color: var(--font-disable-color);
  line-height: 1.45;
  text-align: center;
}

.marketplace-connect-form__benefit strong {
  font-weight: 600;
}

.marketplace-connect-form__timestamp {
  color: var(--text-secondery-color);
  font-size: var(--font-helper);
  font-variant-numeric: tabular-nums;
}

.marketplace-connect-sheet__actions {
  flex: 0 0 auto;
  padding: 16px 0 calc(16px + env(safe-area-inset-bottom));
  border-top: 1px solid var(--crm-border-color);
}

.marketplace-connect-sheet__actions.is-stacked {
  display: grid;
  gap: 12px;
}

.marketplace-connect-detail {
  padding: 24px 22px;
}

.marketplace-connect-detail__list {
  display: grid;
  gap: 16px;
  margin: 0;
}

.marketplace-connect-detail__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
  gap: 16px;
  align-items: center;
}

.marketplace-connect-detail__row dt,
.marketplace-connect-detail__row dd {
  margin: 0;
}

.marketplace-connect-detail__row dt {
  color: var(--text-secondery-color);
}

.marketplace-connect-detail__row dd {
  overflow-wrap: anywhere;
  text-align: right;
}

.marketplace-connect-detail__status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 999px;
  background: #2dbd5b;
  color: #ffffff;
  font-size: var(--font-body);
  font-weight: 600;
}

@media (max-width: 360px) {
  .marketplace-connect-sheet {
    max-height: calc(100dvh - 48px - env(safe-area-inset-top));
  }

  .marketplace-connect-sheet__header {
    padding-top: 16px;
  }

  .marketplace-connect-form {
    gap: 16px;
    padding-top: 24px;
  }
}
</style>
